import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Login from '../components/common/Login';
import ChainDropdown from './ChainDropdown';

function getChainFromUrl() {
  const path = window.location.pathname; // e.g., "/ethereum", "/base", "/ethereum/swap"
  const segments = path.split("/").filter(Boolean);
  const firstSegment = segments[0];
  if (firstSegment === "ethereum") {
    return firstSegment;
  }
  return null;
}

export default function Navbar({updateSigner}) {
  const location = useLocation();
  const chain = getChainFromUrl() || 'ethereum'; // default to ethereum
  
  // State for mobile menu
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSigner, setCurrentSigner] = useState(null);

  // Update the signer when it changes
  const handleSignerUpdate = (signer) => {
    setCurrentSigner(signer);
    updateSigner(signer);
  };

  return (
    <div className="navbar fixed top-0 left-0 w-full z-50 h-16 flex items-center px-3 smm:px-4 xl:px-12 pt-1   border-b border-white/10">
      <div className="w-full h-full flex items-center justify-between">
        
        {/* Left part (Mobile: Hamburger + Logo, Desktop: Logo + Nav) */}
        <div className="flex items-center">
          {/* Mobile Menu Button (visible on small screens) */}
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className="smm:hidden focus:outline-none text-gray-300 hover:text-white mr-2"
          >
            {/* Hamburger Icon */}
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" 
                d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>

          {/* Logo */}
          <Link to={`/${chain}`} className="  items-center hidden smm:flex">
            <img
              className="w-auto h-5 smm:h-7"
              src="/EthervistaSmall.png"
              alt="Ethervistaa"
            />
          </Link>
          <Link to={`/${chain}`} className='hidden xl:flex items-center'>
            <h1 className='pl-2 kanit-light font-[500] text-[19px] text-[#ffffff]'>ETHERVISTA</h1>
          </Link>

          {/* Desktop Nav Links (hidden on small screens) */}
          <ul className="hidden smm:flex justify-center smm:gap-1 mt-[2px] kanit-light font-[400] ml-[5px] mdd:ml-[20px] lg:ml-[26px] items-center text-[9px] xxxs:text-[11px] xxs:text-[12px] xs:text-[14px] smm:text-[15px] lg:text-[14px] text-[#cfcfcf]">
          <li className="relative group">
              <Link 
                to={`/${chain}/lending`} 
                className={`px-2 lg:px-4 p-2  rounded-xl flex items-center gap-[10px] py-1 group text-[#dfdfdf] hover:text-[#fbfbfb] 
                ${location.pathname === `/${chain}/lending` ? "text-[#f4f4f4]" : ""}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <span className=''><img src='/eulend2.png' className='opacity-70 group-hover:opacity-90 h-[14px] w-[14px]' alt='Eulend Icon' /></span>
                <span className='inter-500 font-[500] text-[15px]'>Eulend</span>
              </Link>
            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-black border border-[#333] text-xs text-[#bdbdbd] rounded-none px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
            Eulend is Ethervista’s ETH-only lending engine: deposit ETH once, earn from all pool swaps + LP rewards. Borrowers match it with just their stable-coins, keep custody, and share in the yield. Feeless flash-loans turbo-charge volume and returns for everyone.
            </div>
          </li>
            <li className="relative group">
              <Link 
                to={`/${chain}/swap`} 
                className={`px-2 lg:px-4 py-1 hover:bg-white/0 flex items-center group gap-[10px] rounded-lg text-[#dfdfdf] hover:text-[#fbfbfb] 
                ${location.pathname === "/swap" ? "text-[#f4f4f4]" : ""}`}
              >
              <span className=''><img src='/swap.png' className='opacity-80 group-hover:opacity-100 h-[14px] w-[14px]' alt='Swap Icon' /></span>
              <span className='inter-500 font-[500] text-[15px]'>Swap</span>
              </Link>
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 bg-black border border-[#333] text-xs text-[#bdbdbd] rounded-none px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
              Ethervista is the first DEX where every swap pays fees in ETH, not tokens. Creators set their own ETH fee to fund on-chain logic, LPs earn ETH on every trade—even with locked liquidity—and every new pair launches with a 5-day lock that helps protect users from rug-pulls.
              </div>
            </li>
            <li className="relative group px-2 lg:px-4 hover:bg-white/0 rounded-lg text-[#dfdfdf] hover:text-[#fbfbfb] cursor-pointer">
              <div className="flex items-center">
                <img src='/staking.png' className='opacity-70 group-hover:opacity-90 h-[14px] w-[14px] mr-2' alt='Stake Icon' />
                <span className='inter-500 font-[500] text-[15px]'>Stake</span>
                <svg
                  className={`ml-[6px]  text-[6px] smm:text-[7px] w-[7px] `}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 12 6"
                >
                  <path d="M1 1L6 5L11 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <ul className="absolute left-0  top-5 inter-500 font-[400] bg-[#0e1018] border border-[#333333] rounded-sm w-56 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-opacity duration-200">
                {chain === "ethereum" && (
                  <li>
                    <Link 
                      to={`/${chain}/hardstake`} 
                      className={`block px-4 py-2 text-[12px] text-[#cfcfcf] hover:bg-[#222222a5] hover:text-[#f4f4f4]`}
                    >
                      <div className="font-[500] text-[13px] text-[#f4f4f4]">Hardstake</div>
                      <div className="text-xs text-[#bdbdbd] mt-[2px] ml-[2px]">
                      Stake your VISTA tokens to earn ETH rewards generated from protocol fees on every swap
                      </div>
                    </Link>
                  </li>
                )}
                <li>
                  <Link 
                    to={`/${chain}/hardlock`} 
                    className={`block px-4 py-2 text-[12px] text-[#cfcfcf] hover:bg-[#222222a5] hover:text-[#f4f4f4]`}
                  >
                    <div className="font-[500] text-[13px] text-[#f4f4f4]">Hardlock</div>
                    <div className="text-xs text-[#bdbdbd] mt-[2px] ml-[2px]">
                    Securely lock your liquidity while continuing to earn ETH rewards. 
                    </div>
                  </Link>
                </li>
              </ul>
            </li>
            <li className="relative group px-2 lg:px-4 lg:pl-2 hover:bg-white/0 rounded-lg text-[#dfdfdf] hover:text-[#fbfbfb] cursor-pointer">
              <div className="flex items-center">
              <img src='/liquidity.png' className='opacity-80 group-hover:opacity-90 h-[14px] w-[15px] mr-1' alt='Liquidity Icon' />
              <span className='inter-500 font-[500] text-[15px]'>Liquidity</span>
                <svg
                  className={`ml-[6px]  text-[6px] smm:text-[7px] w-[7px] `}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 12 6"
                >
                  <path d="M1 1L6 5L11 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <ul className="absolute left-0 top-5  inter-500 font-[400] bg-[#0e1018] border border-[#333333] rounded-sm w-56 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-opacity duration-200">
              <li>
                  <Link 
                    to={`/${chain}/`} 
                    className={`block px-4 py-2 text-[12px] text-[#cfcfcf] hover:bg-[#222222a5] hover:text-[#f4f4f4] 
                    `}
                  >
                    <div className="font-[500] text-[13px] text-[#f4f4f4]">Pools</div>
                    <div className="text-xs text-[#bdbdbd] mt-[2px] ml-[2px]">
                      Provide liquidity to earn rewards and support decentralized trading.
                    </div>
                  </Link>
                </li>
              <li>
                  <Link 
                    to={`/${chain}/create-token`} 
                    className={`block px-4 py-2 text-[12px] text-[#cfcfcf] hover:bg-[#222222a5] hover:text-[#f4f4f4] 
                    `}
                  >
                    <div className="font-[500] text-[13px] text-[#f4f4f4]">Create Token</div>
                    <div className="text-xs text-[#bdbdbd] mt-[2px] ml-[2px]">
                      Get your token created by our team with full assistance, free of charge!
                    </div>
                  </Link>
               </li>
              <li>
                  <Link 
                    to={`/${chain}/launch`} 
                    className={`block px-4 py-2 text-[12px] text-[#cfcfcf] hover:bg-[#222222a5] hover:text-[#f4f4f4] 
                    `}
                  >
                    <div className="font-[500] text-[13px] text-[#f4f4f4]">Launch</div>
                    <div className="text-xs text-[#bdbdbd] mt-[2px] ml-[2px]">
                    Deploy a token by setting custom LP fees and Protocol fees for every buy and sell transaction, while specifying a protocol address that receives ETH from each swap.
                    </div>
                  </Link>
               </li> 

              </ul>
            </li>



            {/* jup style */}
            <li className="relative group px-2 lg:px-4 hover:bg-white/0 rounded-lg  cursor-pointer">
              <div className="flex items-center text-[#dfdfdf] hover:text-[#fbfbfb]">
                <span className='inter-500 font-[500] text-[15px]'>Vistaverse</span>
                <svg
                  className={`ml-[6px]  text-[6px] smm:text-[7px] w-2 `}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 12 6"
                >
                  <path d="M1 1L6 5L11 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <ul className="absolute left-0  pt-4 top-full bg-[#0e1018] border border-[#333333] rounded-lg w-[340px] opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-opacity duration-200">
                
                <li className="hidden mdd:flex gap-4 h-16  justify-start items-center px-2">
                  <a
                    href='https://etherfun.app/' target='_blank' rel="noreferrer"
                    className="px-2 lg:px-4   rounded-sm  hover:bg-white/5 flex justify-start gap-4 py-3"
                  >
                    <span className='text-center my-auto'>
                      <img src={require('../assets/etherfun.png')} alt="Etherfun" className="h-8 w-8 object-contain" />
                    </span>
                    <div className='flex flex-col items-center'>
                      <span className=' w-full text-start '>Etherfun</span>
                      <span className='text-white/40 text-start text-xs'>The #1 Protocol for launching Tokens on Ethereum </span>
                    </div>
                  </a>
                </li>
                <li className="hidden mdd:flex gap-4  h-24  justify-start items-center w-full  px-2">
                  <a
                    href='https://thecryptomap.com/' target='_blank' rel="noreferrer"
                    className="px-2 lg:px-4   rounded-sm  hover:bg-white/5 flex justify-start gap-4 py-5 w-full"
                  >
                    <span className='text-center my-auto'>
                      <img src={require('../assets/cryptomap.png')} alt="Crypto Map" className="h-8 w-8 object-contain" />
                    </span>
                    <div className='flex flex-col items-center'>
                      <span className=' w-full text-start '>The Crypto Map</span>
                      <span className='text-white/40 text-start text-xs'>WorldWide Crypto P2P Marketplace and Crypto-to-Cash Exchange </span>
                    </div>
                  </a>
                </li>
                <div className="h-12 rounded-md flex justify-center space-x-12 text-sm bg-gradient-to-r from-[#ffffff03] to-[#ffffff12] border-t border-white/5 items-center">
                  <a href="https://x.com/ethervista" className="flex flex-col items-center hover:text-white" target="_blank" rel="noreferrer">
                    <i className="fab fa-x-twitter text-lg"></i>
                    <span>Twitter</span>
                  </a>
                  <a href="https://discord.com/invite/7FHKG3aCrp" className="flex flex-col items-center hover:text-white" target="_blank" rel="noreferrer">
                    <i className="fab fa-discord text-lg"></i>
                    <span>Discord</span>
                  </a>
                  <a href="https://t.me/etherfun_trenches" className="flex flex-col items-center hover:text-white" target="_blank" rel="noreferrer">
                    <i className="fab fa-telegram text-lg"></i>
                    <span>Telegram</span>
                  </a>
                </div>

              </ul>
            </li>

          </ul>





        </div>
        
        {/* Right part: Auth + Chain dropdown (always visible) */}
        <div className="flex items-center gap-2 xs:gap-3">
          <a href='https://docs.ethervista.app/' className='' rel="noreferrer" target='_blank'>
            <img alt='Docs' className='w-4 h-4 mr-1 text-white' src='/whitepaper.svg' />
          </a>
          <a href='https://x.com/ethervista' className='hidden  xl:hidden' rel="noreferrer" target='_blank'>
            <img alt='x' className='w-4 h-4 mr-4 text-gray-300 ' src='/xicon.png' />
          </a>
          <div className="">
            <ChainDropdown />
          </div>
          <Login updateSigner={handleSignerUpdate} chain={chain} />

        </div>
      </div>

      {/* Side Drawer for Mobile Navigation */}
      <div 
        className={`fixed top-0 left-0 h-screen w-64 bg-[#0e1018] border-r border-[#333] transform transition-transform duration-300 ease-in-out z-[99999]
        ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="h-16 flex items-center px-4 bg-[#0e1018] border-b border-[#333]">
          <button 
            onClick={() => setIsMenuOpen(false)} 
            className="text-gray-300 hover:text-white focus:outline-none mr-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" 
                d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <Link to={`/${chain}`} className="flex items-center ">
            <img
              className="w-auto h-5 mr-2"
              src="/EthervistaSmall.png"
              alt="Ethervistaa"
            />
            <span className="kanit-light font-[500] text-[16px] text-white">ETHERVISTA</span>
          </Link>
        </div>
        <ul className="flex flex-col px-4 py-4 text-[#cfcfcf] kanit-light font-[400] text-sm">
          {/* Eulend */}
          <li>
              <Link 
                to={`/${chain}/lending`} 
                className={`block py-3 hover:bg-[#222222a5] hover:text-[#f4f4f4] flex items-center gap-3
                ${location.pathname === `/${chain}/lending` ? "text-[#f4f4f4]" : ""}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <img src='/eulend2.png' className='opacity-70 h-[16px] w-[16px]' alt='Eulend Icon' />
                <span className='inter-500 font-[500] text-[15px]'>Eulend</span>
              </Link>
          </li>

          {/* Swap */}
          <li>
            <Link 
              to={`/${chain}/swap`} 
              className={`block py-3 hover:bg-[#222222a5] hover:text-[#f4f4f4] flex items-center gap-3
              ${location.pathname === `/${chain}/swap` ? "text-[#f4f4f4]" : ""}`}
              onClick={() => setIsMenuOpen(false)}
            >
              <img src='/swap.png' className='opacity-80 h-[16px] w-[16px]' alt='Swap Icon' />
              <span className='inter-500 font-[500] text-[15px]'>Swap</span>
            </Link>
          </li>

          {/* Staking Section */}
          <li className="py-2">
            <div className="flex items-center gap-3 py-2 text-[#dfdfdf]">
              <img src='/staking.png' className='opacity-70 h-[16px] w-[16px]' alt='Stake Icon' />
              <span className='inter-500 font-[500] text-[15px]'>Stake</span>
            </div>
            <ul className="ml-6 space-y-1">
              {chain === "ethereum" && (
                <li>
                  <Link 
                    to={`/${chain}/hardstake`} 
                    className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd]"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Hardstake
                  </Link>
                </li>
              )}
              <li>
                <Link 
                  to={`/${chain}/hardlock`} 
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Hardlock
                </Link>
              </li>
            </ul>
          </li>

          {/* Liquidity Section */}
          <li className="py-2">
            <div className="flex items-center gap-3 py-2 text-[#dfdfdf]">
              <img src='/liquidity.png' className='opacity-80 h-[16px] w-[16px]' alt='Liquidity Icon' />
              <span className='inter-500 font-[500] text-[15px]'>Liquidity</span>
            </div>
            <ul className="ml-6 space-y-1">
              <li>
                <Link 
                  to={`/${chain}/`} 
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pools
                </Link>
              </li>
              <li>
                <Link 
                  to={`/${chain}/create-token`} 
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Create Token
                </Link>
              </li>
              <li>
                <Link 
                  to={`/${chain}/launch`} 
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Launch
                </Link>
              </li>
            </ul>
          </li>

          {/* Vistaverse Section */}
          <li className="py-2">
            <div className="flex items-center gap-3 py-2 text-[#dfdfdf]">
              <span className='inter-500 font-[500] text-[15px]'>Vistaverse</span>
            </div>
            <ul className="ml-6 space-y-1">
              <li>
                <a
                  href='https://etherfun.app/' target='_blank' rel="noreferrer"
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd] flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img src={require('../assets/etherfun.png')} alt="Etherfun" className="h-4 w-4 object-contain" />
                  Etherfun
                </a>
              </li>
              <li>
                <a
                  href='https://thecryptomap.com/' target='_blank' rel="noreferrer"
                  className="block py-2 hover:bg-[#222222a5] hover:text-[#f4f4f4] text-[13px] text-[#bdbdbd] flex items-center gap-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <img src={require('../assets/cryptomap.png')} alt="Crypto Map" className="h-4 w-4 object-contain" />
                  The Crypto Map
                </a>
              </li>
            </ul>
          </li>

          {/* Social Links */}
          <li className="py-2">
            <div className="flex justify-center space-x-6 text-sm py-2">
              <a href="https://x.com/ethervista" className="flex flex-col items-center hover:text-white text-[#bdbdbd]" target="_blank" rel="noreferrer">
                <i className="fab fa-x-twitter text-lg"></i>
                <span className="text-xs">Twitter</span>
              </a>
              <a href="https://discord.com/invite/7FHKG3aCrp" className="flex flex-col items-center hover:text-white text-[#bdbdbd]" target="_blank" rel="noreferrer">
                <i className="fab fa-discord text-lg"></i>
                <span className="text-xs">Discord</span>
              </a>
              <a href="https://t.me/etherfun_trenches" className="flex flex-col items-center hover:text-white text-[#bdbdbd]" target="_blank" rel="noreferrer">
                <i className="fab fa-telegram text-lg"></i>
                <span className="text-xs">Telegram</span>
              </a>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
